import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Star, ChevronLeft, User, Package, MessageSquare, ThumbsUp, ThumbsDown, AlertCircle, RefreshCw, Filter, TrendingUp, Award, ShieldCheck } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleRatings = () => {
  const { customer } = useCustomerAuth()
  const [activeTab, setActiveTab] = useState('to-rate')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [ordersToRate, setOrdersToRate] = useState([])
  const [myRatings, setMyRatings] = useState([])
  const [sellerRatings, setSellerRatings] = useState(null)
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [submittingRating, setSubmittingRating] = useState(false)

  // Rating form
  const [ratingForm, setRatingForm] = useState({
    product_rating: 0,
    seller_rating: 0,
    delivery_rating: 0,
    review_text: '',
    would_recommend: true
  })

  const getMgCode = (tenantDb) => {
    if (!tenantDb) return null
    const prefix = 'mgfina6_tenant_'
    if (tenantDb.startsWith(prefix)) return tenantDb.substring(prefix.length)
    const parts = tenantDb.split('_')
    return parts.length > 1 ? parts[1] : tenantDb
  }

  const userMemNo = customer?.memberNo
  const userMgCode = getMgCode(customer?.tenantDb)

  useEffect(() => {
    if (customer) fetchRatingData()
  }, [customer, activeTab])

  const fetchRatingData = async () => {
    if (!userMemNo || !userMgCode) return
    setLoading(true)
    setError('')

    try {
      if (activeTab === 'to-rate') {
        const response = await apiService.makeAuthenticatedRequest('KATALE-GET-UNRATED-ORDERS', {
          mem_no: userMemNo,
          mg_code: userMgCode
        })
        if (response.status === 'SUCCESS') setOrdersToRate(response.data || [])
      } else if (activeTab === 'my-reviews') {
        const response = await apiService.makeAuthenticatedRequest('KATALE-GET-MY-RATINGS', {
          mem_no: userMemNo,
          mg_code: userMgCode
        })
        if (response.status === 'SUCCESS') setMyRatings(response.data || [])
      } else if (activeTab === 'my-seller-rating') {
        const response = await apiService.makeAuthenticatedRequest('KATALE-GET-SELLER-RATINGS', {
          seller_mem_no: userMemNo,
          seller_mg_code: userMgCode
        })
        if (response.status === 'SUCCESS') setSellerRatings(response.data || null)
      }
    } catch (err) {
      console.error('Failed to fetch ratings:', err)
      setError('Failed to load rating data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const openRatingModal = (order) => {
    setSelectedOrder(order)
    setRatingForm({ product_rating: 0, seller_rating: 0, delivery_rating: 0, review_text: '', would_recommend: true })
    setShowRatingModal(true)
  }

  const handleSubmitRating = async () => {
    if (ratingForm.product_rating === 0 || ratingForm.seller_rating === 0) {
      alert('Please provide at least a product and seller rating')
      return
    }
    setSubmittingRating(true)
    try {
      const response = await apiService.makeAuthenticatedRequest('KATALE-SUBMIT-RATING', {
        order_id: selectedOrder.id,
        buyer_mem_no: userMemNo,
        buyer_mg_code: userMgCode,
        product_rating: ratingForm.product_rating,
        seller_rating: ratingForm.seller_rating,
        delivery_rating: ratingForm.delivery_rating || null,
        review_text: ratingForm.review_text || null,
        would_recommend: ratingForm.would_recommend
      })
      if (response.status === 'SUCCESS') {
        setShowRatingModal(false)
        setSelectedOrder(null)
        fetchRatingData()
      } else {
        alert(response.message || 'Failed to submit rating')
      }
    } catch (err) {
      alert('Error submitting rating: ' + err.message)
    } finally {
      setSubmittingRating(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', minimumFractionDigits: 0 }).format(amount || 0)
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-UG', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'

  const StarRating = ({ rating, onRate, size = 'md', interactive = false }) => {
    const [hovered, setHovered] = useState(0)
    const sizeClass = size === 'lg' ? 'w-8 h-8' : size === 'sm' ? 'w-4 h-4' : 'w-6 h-6'

    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            onMouseEnter={() => interactive && setHovered(star)}
            onMouseLeave={() => interactive && setHovered(0)}
            className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : 'cursor-default'}
            disabled={!interactive}
          >
            <Star
              className={`${sizeClass} ${
                star <= (hovered || rating)
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300'
              } transition-colors`}
            />
          </button>
        ))}
        {rating > 0 && <span className="ml-1 text-sm font-medium text-gray-600">{rating.toFixed(1)}</span>}
      </div>
    )
  }

  const RatingBar = ({ label, value, total }) => {
    const pct = total > 0 ? (value / total) * 100 : 0
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="w-8 text-right text-gray-600">{label}</span>
        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div className="bg-yellow-400 rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="w-8 text-gray-500">{value}</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/katale" className="text-gray-600 hover:text-gray-900 mr-4">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <Star className="h-6 w-6 text-yellow-500 mr-2" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Ratings & Reviews</h1>
                <p className="text-sm text-gray-500">Rate products, view seller reputation</p>
              </div>
            </div>
            <button onClick={fetchRatingData} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
          {[
            { id: 'to-rate', label: 'Orders to Rate', icon: Star, badge: ordersToRate.length || null },
            { id: 'my-reviews', label: 'My Reviews', icon: MessageSquare },
            { id: 'my-seller-rating', label: 'My Seller Rating', icon: Award }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500" />
          </div>
        ) : (
          <>
            {/* Orders To Rate */}
            {activeTab === 'to-rate' && (
              <div className="space-y-4">
                {ordersToRate.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
                    <p className="text-gray-500">No delivered orders waiting for your review.</p>
                  </div>
                ) : (
                  ordersToRate.map(order => (
                    <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-gray-900">{order.order_number || `Order #${order.id}`}</p>
                          <p className="text-sm text-gray-500 mt-1">Delivered: {formatDate(order.delivered_at || order.updated_at)}</p>
                          <div className="mt-2 space-y-1">
                            {(order.items || []).map((item, idx) => (
                              <p key={idx} className="text-sm text-gray-600">
                                {item.quantity}x {item.product_name} — {formatCurrency(item.item_subtotal)}
                              </p>
                            ))}
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            Seller: <span className="font-medium text-gray-700">{order.seller_mg_code || order.items?.[0]?.seller_mg_code || 'Seller'}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">{formatCurrency(order.total_amount)}</p>
                          <button
                            onClick={() => openRatingModal(order)}
                            className="mt-3 flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm font-medium"
                          >
                            <Star className="w-4 h-4" /> Rate Order
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* My Reviews */}
            {activeTab === 'my-reviews' && (
              <div className="space-y-4">
                {myRatings.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                    <p className="text-gray-500">Your ratings and reviews will appear here.</p>
                  </div>
                ) : (
                  myRatings.map((rating, idx) => (
                    <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-gray-900">{rating.order_number || `Order #${rating.order_id}`}</p>
                          <p className="text-xs text-gray-500 mb-3">{formatDate(rating.created_at)}</p>
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-16">Product</span>
                              <StarRating rating={rating.product_rating} size="sm" />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-16">Seller</span>
                              <StarRating rating={rating.seller_rating} size="sm" />
                            </div>
                            {rating.delivery_rating > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 w-16">Delivery</span>
                                <StarRating rating={rating.delivery_rating} size="sm" />
                              </div>
                            )}
                          </div>
                          {rating.review_text && (
                            <p className="mt-3 text-sm text-gray-600 italic">"{rating.review_text}"</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {rating.would_recommend ? (
                            <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                              <ThumbsUp className="w-3 h-3" /> Recommended
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                              <ThumbsDown className="w-3 h-3" /> Not recommended
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* My Seller Rating */}
            {activeTab === 'my-seller-rating' && (
              <div>
                {!sellerRatings || sellerRatings.total_ratings === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No seller ratings yet</h3>
                    <p className="text-gray-500">When buyers rate your products, your seller profile will appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Overview */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <div className="flex flex-col md:flex-row gap-8">
                        {/* Big Rating */}
                        <div className="text-center">
                          <p className="text-5xl font-bold text-gray-900">{sellerRatings.average_rating?.toFixed(1) || '0.0'}</p>
                          <StarRating rating={sellerRatings.average_rating || 0} size="lg" />
                          <p className="text-sm text-gray-500 mt-2">{sellerRatings.total_ratings} rating{sellerRatings.total_ratings !== 1 ? 's' : ''}</p>
                          {sellerRatings.recommendation_rate > 0 && (
                            <div className="mt-3 flex items-center justify-center gap-1 text-sm">
                              <ThumbsUp className="w-4 h-4 text-green-600" />
                              <span className="text-green-600 font-medium">{sellerRatings.recommendation_rate}%</span>
                              <span className="text-gray-500">would recommend</span>
                            </div>
                          )}
                        </div>

                        {/* Distribution */}
                        <div className="flex-1 space-y-2">
                          {[5, 4, 3, 2, 1].map(star => (
                            <RatingBar
                              key={star}
                              label={star}
                              value={sellerRatings.rating_distribution?.[star] || 0}
                              total={sellerRatings.total_ratings}
                            />
                          ))}
                        </div>

                        {/* Category Breakdown */}
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Product Quality</p>
                            <div className="flex items-center gap-2 mt-1">
                              <StarRating rating={sellerRatings.avg_product_rating || 0} size="sm" />
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Seller Service</p>
                            <div className="flex items-center gap-2 mt-1">
                              <StarRating rating={sellerRatings.avg_seller_rating || 0} size="sm" />
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Delivery</p>
                            <div className="flex items-center gap-2 mt-1">
                              <StarRating rating={sellerRatings.avg_delivery_rating || 0} size="sm" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Seller Badge */}
                    {sellerRatings.average_rating >= 4.0 && sellerRatings.total_ratings >= 5 && (
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-5 flex items-center gap-4">
                        <div className="p-3 bg-yellow-100 rounded-xl">
                          <ShieldCheck className="h-8 w-8 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-yellow-900">Trusted Seller</h3>
                          <p className="text-sm text-yellow-700">You've earned the Trusted Seller badge with a {sellerRatings.average_rating.toFixed(1)}★ average from {sellerRatings.total_ratings} reviews.</p>
                        </div>
                      </div>
                    )}

                    {/* Recent Reviews */}
                    {sellerRatings.recent_reviews?.length > 0 && (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900">Recent Reviews from Buyers</h3>
                        </div>
                        <div className="divide-y divide-gray-200">
                          {sellerRatings.recent_reviews.map((review, idx) => (
                            <div key={idx} className="px-6 py-4">
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-900">{review.buyer_name || 'Buyer'}</span>
                                    <span className="text-xs text-gray-400">{formatDate(review.created_at)}</span>
                                  </div>
                                  <StarRating rating={review.product_rating} size="sm" />
                                  {review.review_text && (
                                    <p className="mt-2 text-sm text-gray-600">"{review.review_text}"</p>
                                  )}
                                </div>
                                {review.would_recommend ? (
                                  <ThumbsUp className="w-5 h-5 text-green-500 flex-shrink-0" />
                                ) : (
                                  <ThumbsDown className="w-5 h-5 text-red-500 flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Rating Modal */}
      {showRatingModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Rate Your Order</h3>
              <p className="text-sm text-gray-500 mb-6">{selectedOrder.order_number || `Order #${selectedOrder.id}`}</p>

              <div className="space-y-6">
                {/* Product Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Quality *</label>
                  <StarRating
                    rating={ratingForm.product_rating}
                    onRate={(v) => setRatingForm({ ...ratingForm, product_rating: v })}
                    size="lg"
                    interactive
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {ratingForm.product_rating === 0 ? 'Tap a star' : ['', 'Poor', 'Below average', 'Average', 'Good', 'Excellent'][ratingForm.product_rating]}
                  </p>
                </div>

                {/* Seller Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seller Service *</label>
                  <StarRating
                    rating={ratingForm.seller_rating}
                    onRate={(v) => setRatingForm({ ...ratingForm, seller_rating: v })}
                    size="lg"
                    interactive
                  />
                </div>

                {/* Delivery Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Experience</label>
                  <StarRating
                    rating={ratingForm.delivery_rating}
                    onRate={(v) => setRatingForm({ ...ratingForm, delivery_rating: v })}
                    size="lg"
                    interactive
                  />
                </div>

                {/* Review Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Review (optional)</label>
                  <textarea
                    value={ratingForm.review_text}
                    onChange={(e) => setRatingForm({ ...ratingForm, review_text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    rows={3}
                    placeholder="Tell other buyers about your experience..."
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-400 mt-1">{ratingForm.review_text.length}/500</p>
                </div>

                {/* Would Recommend */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Would you recommend this seller?</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setRatingForm({ ...ratingForm, would_recommend: true })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition ${
                        ratingForm.would_recommend
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <ThumbsUp className="w-5 h-5" /> Yes
                    </button>
                    <button
                      onClick={() => setRatingForm({ ...ratingForm, would_recommend: false })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition ${
                        !ratingForm.would_recommend
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <ThumbsDown className="w-5 h-5" /> No
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => { setShowRatingModal(false); setSelectedOrder(null) }}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRating}
                disabled={submittingRating || ratingForm.product_rating === 0 || ratingForm.seller_rating === 0}
                className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center gap-2"
              >
                {submittingRating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Star className="w-4 h-4" />
                )}
                Submit Rating
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KataleRatings
